// Availability entry, overview, edit and withdrawal surfaces (AVL-001–AVL-019).
//
// These components render server-decided affordances. They never infer a role,
// never turn availability into a commitment, and never compose user-facing copy.

import {
  Button,
  CalendarWeek,
  Card,
  Dialog,
  EmptyState,
  Select,
  StatusMessage,
  TextInput,
  targetSize,
  type CalendarDayProps,
} from "@herefor/design-system";
import {
  instant,
  toWallClock,
  type BlockDefinitions,
  type TimeZoneId,
} from "@herefor/domain-kernel";
import {
  AVAILABILITY_EXPIRY_MESSAGES as EXPIRY_COPY,
  AVAILABILITY_SURFACE_MESSAGES as COPY,
} from "@herefor/product-language";
import { useMemo, useState, type SyntheticEvent } from "react";

import {
  availabilityWeek,
  availabilityWeekEnd,
  resolveAvailabilityDraft,
  type AvailabilityDraft,
  type AvailabilityDraftIssue,
  type AvailabilityHelpMode,
  type AvailabilityMutationEntry,
  type AvailabilitySourceChannel,
  type BlockSelection,
} from "./availabilityData";

export type AvailabilityWriteMode = "self" | "proxy";

export interface AvailabilityPersonOption {
  readonly personId: string;
  readonly name: string;
}

export interface AvailabilitySubmission {
  readonly mode: AvailabilityWriteMode;
  readonly weekStartLocal: string;
  readonly entries: readonly AvailabilityMutationEntry[];
  readonly personId?: string;
  readonly sourceChannel?: AvailabilitySourceChannel;
}

export type AvailabilityOutcome =
  | { readonly status: "idle" | "working" }
  | { readonly status: "saved"; readonly action: "record" | "edit" | "withdraw" }
  | { readonly status: "refused"; readonly message: string };

export interface AvailabilityWindowView {
  readonly availabilityWindowId: string;
  readonly startsAtLocal: string;
  readonly endsAtLocal: string;
  readonly dateLabel: string;
  readonly windowLabel: string;
  readonly helpModeLabels: readonly string[];
  readonly helpModes: readonly AvailabilityHelpMode[];
}

export interface AvailabilityCommitmentNotice {
  readonly count: number;
  readonly dayLabel: string;
}

export interface AvailabilityBatchView {
  readonly availabilityBatchId: string;
  readonly personId: string;
  readonly personName: string;
  readonly weekStartLocal: string;
  readonly weekEndLabel: string;
  readonly expiryDateLocal: string;
  readonly expiryDateLabel: string;
  readonly expiresAt: number;
  readonly expired: boolean;
  readonly source: AvailabilityWriteMode;
  readonly recordedByName: string | null;
  readonly sourceChannel: string | null;
  readonly windows: readonly AvailabilityWindowView[];
  readonly editMode: AvailabilityWriteMode | null;
  readonly withdrawMode: AvailabilityWriteMode | null;
  readonly commitmentNotice: AvailabilityCommitmentNotice | null;
}

export interface AvailabilityRenewalView {
  readonly endLabel: string;
  readonly expired: boolean;
  readonly nextWeekStartLocal: string;
}

/** A calm invitation only. The button opens a new dated batch; it never extends one. */
export function AvailabilityRenewalPrompt({
  actionsDisabled,
  onRenew,
  renewal,
}: {
  readonly actionsDisabled: boolean;
  readonly onRenew: (nextWeekStartLocal: string) => void;
  readonly renewal: AvailabilityRenewalView;
}) {
  return (
    <section aria-labelledby="availability-renewal-heading" className="availability-renewal">
      <Card>
        <h2 id="availability-renewal-heading">
          {renewal.expired
            ? EXPIRY_COPY.endedHeading(renewal.endLabel)
            : EXPIRY_COPY.endingHeading(renewal.endLabel)}
        </h2>
        <p>{EXPIRY_COPY.promptBody}</p>
        <Button
          disabled={actionsDisabled}
          onClick={() => {
            onRenew(renewal.nextWeekStartLocal);
          }}
          variant="secondary"
        >
          {EXPIRY_COPY.addMore}
        </Button>
      </Card>
    </section>
  );
}

export function AvailabilityEntry({
  actionsDisabled,
  blockDefinitions,
  canRecordProxy,
  canRecordSelf,
  initialWeekStart,
  onSave,
  outcome,
  people,
  timeZone,
}: {
  readonly actionsDisabled: boolean;
  readonly blockDefinitions: BlockDefinitions;
  readonly canRecordProxy: boolean;
  readonly canRecordSelf: boolean;
  readonly initialWeekStart: string;
  readonly onSave: (submission: AvailabilitySubmission) => void;
  readonly outcome: AvailabilityOutcome;
  readonly people: readonly AvailabilityPersonOption[];
  readonly timeZone: TimeZoneId;
}) {
  const firstMode: AvailabilityWriteMode = canRecordSelf ? "self" : "proxy";
  const [writeMode, setWriteMode] = useState<AvailabilityWriteMode>(firstMode);
  const [editorMode, setEditorMode] = useState<AvailabilityDraft["mode"]>("blocks");
  const [weekStartLocal, setWeekStartLocal] = useState(initialWeekStart);
  const [selectedBlocks, setSelectedBlocks] = useState<readonly BlockSelection[]>([]);
  const [exactStartsAtLocal, setExactStartsAtLocal] = useState("");
  const [exactEndsAtLocal, setExactEndsAtLocal] = useState("");
  const [helpModes, setHelpModes] = useState<readonly AvailabilityHelpMode[]>([]);
  const [personId, setPersonId] = useState(people[0]?.personId ?? "");
  const [sourceChannel, setSourceChannel] = useState<AvailabilitySourceChannel>("message");
  const [consentConfirmed, setConsentConfirmed] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const busy = actionsDisabled || outcome.status === "working";

  const days = useMemo(
    () => safeWeek(weekStartLocal, timeZone, blockDefinitions),
    [blockDefinitions, timeZone, weekStartLocal],
  );
  const weekEnd = safeWeekEnd(weekStartLocal);
  const weekProblem =
    problem === COPY.errors.invalid_week_start || problem === COPY.errors.week_must_start_monday
      ? problem
      : undefined;

  const submit = (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    const resolved = resolveAvailabilityDraft(
      {
        mode: editorMode,
        weekStartLocal,
        selectedBlocks,
        exactStartsAtLocal,
        exactEndsAtLocal,
        helpModes,
      },
      timeZone,
      blockDefinitions,
    );
    if (!resolved.ok) {
      setProblem(messageForIssue(resolved.issue));
      return;
    }
    if (writeMode === "proxy" && personId === "") {
      setProblem(COPY.errors.choosePerson);
      return;
    }
    if (writeMode === "proxy" && !consentConfirmed) {
      setProblem(COPY.errors.confirmProxyConsent);
      return;
    }

    setProblem(null);
    onSave({
      mode: writeMode,
      weekStartLocal: resolved.weekStartLocal,
      entries: resolved.entries,
      ...(writeMode === "proxy" ? { personId, sourceChannel } : {}),
    });
  };

  return (
    <section aria-labelledby="availability-entry-heading" className="availability-entry">
      <Card>
        <h2 id="availability-entry-heading">{COPY.entryHeading}</h2>
        <p>{COPY.meaning}</p>
        <form aria-busy={outcome.status === "working"} onSubmit={submit}>
          {canRecordSelf && canRecordProxy ? (
            <fieldset className="availability-fieldset">
              <legend>{COPY.subjectLegend}</legend>
              <Radio
                checked={writeMode === "self"}
                disabled={busy}
                label={COPY.subject.self}
                name="availability-subject"
                onChange={() => {
                  setWriteMode("self");
                  setProblem(null);
                }}
                value="self"
              />
              <Radio
                checked={writeMode === "proxy"}
                disabled={busy}
                label={COPY.subject.proxy}
                name="availability-subject"
                onChange={() => {
                  setWriteMode("proxy");
                  setProblem(null);
                }}
                value="proxy"
              />
            </fieldset>
          ) : null}

          {writeMode === "proxy" ? (
            <fieldset className="availability-fieldset availability-proxy-fields">
              <legend>{COPY.proxyHeading}</legend>
              <p>{COPY.proxyMeaning}</p>
              <Select
                disabled={busy}
                label={COPY.proxyPersonLabel}
                onChange={(event) => {
                  setPersonId(event.currentTarget.value);
                }}
                options={people.map((person) => ({ value: person.personId, label: person.name }))}
                value={personId}
              />
              <Select
                disabled={busy}
                label={COPY.proxySourceLabel}
                onChange={(event) => {
                  setSourceChannel(asSourceChannel(event.currentTarget.value));
                }}
                options={SOURCE_OPTIONS}
                value={sourceChannel}
              />
              <Checkbox
                checked={consentConfirmed}
                disabled={busy}
                label={COPY.proxyConsent}
                onChange={setConsentConfirmed}
              />
            </fieldset>
          ) : null}

          <TextInput
            disabled={busy}
            error={weekProblem}
            label={COPY.weekStartLabel}
            onChange={(event) => {
              setWeekStartLocal(event.currentTarget.value);
              setSelectedBlocks([]);
              setProblem(null);
            }}
            required
            type="date"
            value={weekStartLocal}
          />
          {weekEnd === null ? null : (
            <StatusMessage tone="info">{COPY.explicitEnd(formatDate(weekEnd))}</StatusMessage>
          )}

          <fieldset className="availability-fieldset availability-mode-picker">
            <legend>{COPY.entryModeLegend}</legend>
            <Radio
              checked={editorMode === "blocks"}
              disabled={busy}
              label={COPY.blockMode}
              name="availability-entry-mode"
              onChange={() => {
                setEditorMode("blocks");
                setProblem(null);
              }}
              value="blocks"
            />
            <Radio
              checked={editorMode === "exact"}
              disabled={busy}
              label={COPY.exactMode}
              name="availability-entry-mode"
              onChange={() => {
                setEditorMode("exact");
                setProblem(null);
              }}
              value="exact"
            />
          </fieldset>

          {editorMode === "blocks" ? (
            <CalendarWeek
              days={calendarDays(days, selectedBlocks, busy, setSelectedBlocks, timeZone)}
              label={COPY.weekRegion(
                formatDate(weekStartLocal),
                weekEnd === null ? "" : formatDate(weekEnd),
              )}
            />
          ) : (
            <div className="availability-exact-times">
              <TextInput
                disabled={busy}
                label={COPY.exactStartLabel}
                onChange={(event) => {
                  setExactStartsAtLocal(event.currentTarget.value);
                  setProblem(null);
                }}
                required
                type="datetime-local"
                value={exactStartsAtLocal}
              />
              <TextInput
                disabled={busy}
                label={COPY.exactEndLabel}
                onChange={(event) => {
                  setExactEndsAtLocal(event.currentTarget.value);
                  setProblem(null);
                }}
                required
                type="datetime-local"
                value={exactEndsAtLocal}
              />
            </div>
          )}

          <fieldset className="availability-fieldset availability-help-modes">
            <legend>{COPY.helpModeLegend}</legend>
            {HELP_MODE_OPTIONS.map((option) => (
              <Checkbox
                checked={helpModes.includes(option.value)}
                disabled={busy}
                key={option.value}
                label={option.label}
                onChange={(checked) => {
                  setHelpModes(toggleValue(helpModes, option.value, checked));
                  setProblem(null);
                }}
              />
            ))}
          </fieldset>

          {problem === null || weekProblem !== undefined ? null : (
            <StatusMessage live="assertive" tone="error">
              {problem}
            </StatusMessage>
          )}
          <Button disabled={busy} type="submit" variant="primary">
            {writeMode === "proxy" ? COPY.saveProxy : COPY.saveSelf}
          </Button>
          <OutcomeMessage outcome={outcome} />
        </form>
      </Card>
    </section>
  );
}

export function AvailabilityOverview({
  batches,
  onEdit,
  onWithdraw,
}: {
  readonly batches: readonly AvailabilityBatchView[];
  readonly onEdit: (batch: AvailabilityBatchView) => void;
  readonly onWithdraw: (batch: AvailabilityBatchView) => void;
}) {
  return (
    <section aria-labelledby="availability-overview-heading" className="availability-overview">
      <h1 id="availability-overview-heading">{COPY.overviewHeading}</h1>
      <p>{COPY.meaning}</p>
      {batches.length === 0 ? (
        <EmptyState description={COPY.emptyBody} title={COPY.emptyTitle} />
      ) : (
        <ul className="availability-overview__list">
          {batches.map((batch) => (
            <li
              className={batch.expired ? "availability-overview__batch--expired" : undefined}
              key={batch.availabilityBatchId}
            >
              <Card title={batch.personName} titleLevel={2}>
                <p className="availability-overview__end">{COPY.ends(batch.weekEndLabel)}</p>
                {batch.expired ? (
                  <p className="availability-overview__history">
                    {EXPIRY_COPY.expiredHistory(batch.expiryDateLabel)}
                  </p>
                ) : null}
                {batch.recordedByName === null || batch.sourceChannel === null ? null : (
                  <p className="availability-overview__provenance">
                    {COPY.provenance(
                      batch.personName,
                      batch.recordedByName,
                      COPY.proxySourceDescription(batch.sourceChannel),
                    )}
                  </p>
                )}
                <ul className="availability-overview__windows">
                  {batch.windows.map((window) => (
                    <li key={window.availabilityWindowId}>
                      <span>{window.dateLabel}</span>
                      <span>{window.windowLabel}</span>
                      <span>{window.helpModeLabels.join(" · ")}</span>
                    </li>
                  ))}
                </ul>
                {batch.expired || batch.editMode === null ? null : (
                  <Button
                    onClick={() => {
                      onEdit(batch);
                    }}
                    variant="secondary"
                  >
                    {COPY.edit}
                  </Button>
                )}
                {batch.expired || batch.withdrawMode === null ? null : (
                  <Button
                    onClick={() => {
                      onWithdraw(batch);
                    }}
                    variant="danger"
                  >
                    {COPY.withdraw}
                  </Button>
                )}
              </Card>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export function WithdrawAvailabilityDialog({
  actionsDisabled,
  batch,
  onClose,
  onConfirm,
}: {
  readonly actionsDisabled: boolean;
  readonly batch: AvailabilityBatchView | null;
  readonly onClose: () => void;
  readonly onConfirm: (sourceChannel?: AvailabilitySourceChannel) => void;
}) {
  const [sourceChannel, setSourceChannel] = useState<AvailabilitySourceChannel>("message");
  const [consentConfirmed, setConsentConfirmed] = useState(false);
  const proxy = batch?.withdrawMode === "proxy";
  const confirmDisabled = actionsDisabled || (proxy && !consentConfirmed);

  return (
    <Dialog
      footer={
        <>
          <Button disabled={actionsDisabled} onClick={onClose} variant="secondary">
            {COPY.cancel}
          </Button>
          <Button
            disabled={confirmDisabled}
            onClick={() => {
              onConfirm(proxy ? sourceChannel : undefined);
            }}
            variant="danger"
          >
            {COPY.confirmWithdraw}
          </Button>
        </>
      }
      onClose={onClose}
      open={batch !== null}
      title={COPY.withdrawHeading}
    >
      <p>{COPY.withdrawMeaning}</p>
      {batch?.commitmentNotice === null || batch?.commitmentNotice === undefined ? null : (
        <StatusMessage tone="warning">
          {COPY.commitmentsStay(
            batch.commitmentNotice.count,
            batch.commitmentNotice.dayLabel,
            batch.withdrawMode === "proxy" ? batch.personName : undefined,
          )}
        </StatusMessage>
      )}
      {proxy ? (
        <fieldset className="availability-fieldset">
          <legend>{COPY.proxyWithdrawalHeading}</legend>
          <Select
            disabled={actionsDisabled}
            label={COPY.proxySourceLabel}
            onChange={(event) => {
              setSourceChannel(asSourceChannel(event.currentTarget.value));
            }}
            options={SOURCE_OPTIONS}
            value={sourceChannel}
          />
          <Checkbox
            checked={consentConfirmed}
            disabled={actionsDisabled}
            label={COPY.proxyWithdrawConsent}
            onChange={setConsentConfirmed}
          />
        </fieldset>
      ) : null}
    </Dialog>
  );
}

export function EditAvailabilityDialog({
  actionsDisabled,
  batch,
  blockDefinitions,
  onClose,
  onSave,
  timeZone,
}: {
  readonly actionsDisabled: boolean;
  readonly batch: AvailabilityBatchView | null;
  readonly blockDefinitions: BlockDefinitions;
  readonly onClose: () => void;
  readonly onSave: (
    entries: readonly AvailabilityMutationEntry[],
    sourceChannel?: AvailabilitySourceChannel,
  ) => void;
  readonly timeZone: TimeZoneId;
}) {
  if (batch === null) return null;
  return (
    <EditAvailabilityDialogContent
      actionsDisabled={actionsDisabled}
      batch={batch}
      blockDefinitions={blockDefinitions}
      key={batch.availabilityBatchId}
      onClose={onClose}
      onSave={onSave}
      timeZone={timeZone}
    />
  );
}

function EditAvailabilityDialogContent({
  actionsDisabled,
  batch,
  blockDefinitions,
  onClose,
  onSave,
  timeZone,
}: {
  readonly actionsDisabled: boolean;
  readonly batch: AvailabilityBatchView;
  readonly blockDefinitions: BlockDefinitions;
  readonly onClose: () => void;
  readonly onSave: (
    entries: readonly AvailabilityMutationEntry[],
    sourceChannel?: AvailabilitySourceChannel,
  ) => void;
  readonly timeZone: TimeZoneId;
}) {
  const [windows, setWindows] = useState(
    batch.windows.map((window) => ({
      startsAtLocal: window.startsAtLocal,
      endsAtLocal: window.endsAtLocal,
      helpModes: window.helpModes,
    })),
  );
  const [sourceChannel, setSourceChannel] = useState<AvailabilitySourceChannel>(
    asSourceChannel(batch.sourceChannel ?? "message"),
  );
  const [consentConfirmed, setConsentConfirmed] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const proxy = batch.editMode === "proxy";

  const submit = () => {
    const entries: AvailabilityMutationEntry[] = [];
    for (const window of windows) {
      const resolved = resolveAvailabilityDraft(
        {
          mode: "exact",
          weekStartLocal: batch.weekStartLocal,
          selectedBlocks: [],
          exactStartsAtLocal: window.startsAtLocal,
          exactEndsAtLocal: window.endsAtLocal,
          helpModes: window.helpModes,
        },
        timeZone,
        blockDefinitions,
      );
      if (!resolved.ok) {
        setProblem(messageForIssue(resolved.issue));
        return;
      }
      entries.push(...resolved.entries);
    }
    if (proxy && !consentConfirmed) {
      setProblem(COPY.errors.confirmProxyConsent);
      return;
    }
    setProblem(null);
    onSave(entries, proxy ? sourceChannel : undefined);
  };

  return (
    <Dialog
      footer={
        <>
          <Button disabled={actionsDisabled} onClick={onClose} variant="secondary">
            {COPY.cancel}
          </Button>
          <Button disabled={actionsDisabled} onClick={submit} variant="primary">
            {COPY.saveChanges}
          </Button>
        </>
      }
      onClose={onClose}
      open
      title={COPY.editHeading(batch.personName)}
    >
      <p>{COPY.editMeaning}</p>
      <StatusMessage tone="info">{COPY.ends(batch.weekEndLabel)}</StatusMessage>
      <div className="availability-edit-windows">
        {windows.map((window, index) => (
          <fieldset
            className="availability-fieldset"
            key={batch.windows[index]?.availabilityWindowId}
          >
            <legend>{COPY.editWindow(index + 1)}</legend>
            <TextInput
              disabled={actionsDisabled}
              label={COPY.exactStartLabel}
              onChange={(event) => {
                setWindows(
                  replaceWindow(windows, index, "startsAtLocal", event.currentTarget.value),
                );
                setProblem(null);
              }}
              required
              type="datetime-local"
              value={window.startsAtLocal}
            />
            <TextInput
              disabled={actionsDisabled}
              label={COPY.exactEndLabel}
              onChange={(event) => {
                setWindows(replaceWindow(windows, index, "endsAtLocal", event.currentTarget.value));
                setProblem(null);
              }}
              required
              type="datetime-local"
              value={window.endsAtLocal}
            />
          </fieldset>
        ))}
      </div>
      {proxy ? (
        <fieldset className="availability-fieldset">
          <legend>{COPY.proxyEditHeading}</legend>
          <Select
            disabled={actionsDisabled}
            label={COPY.proxySourceLabel}
            onChange={(event) => {
              setSourceChannel(asSourceChannel(event.currentTarget.value));
            }}
            options={SOURCE_OPTIONS}
            value={sourceChannel}
          />
          <Checkbox
            checked={consentConfirmed}
            disabled={actionsDisabled}
            label={COPY.proxyEditConsent}
            onChange={setConsentConfirmed}
          />
        </fieldset>
      ) : null}
      {problem === null ? null : (
        <StatusMessage live="assertive" tone="error">
          {problem}
        </StatusMessage>
      )}
    </Dialog>
  );
}

function OutcomeMessage({ outcome }: { readonly outcome: AvailabilityOutcome }) {
  if (outcome.status !== "saved") {
    return outcome.status === "refused" ? (
      <StatusMessage tone="error">{outcome.message}</StatusMessage>
    ) : null;
  }
  const message =
    outcome.action === "record"
      ? COPY.saved
      : outcome.action === "edit"
        ? COPY.updated
        : COPY.withdrawn;
  return <StatusMessage tone="success">{message}</StatusMessage>;
}

function Radio({
  checked,
  disabled,
  label,
  name,
  onChange,
  value,
}: {
  readonly checked: boolean;
  readonly disabled: boolean;
  readonly label: string;
  readonly name: string;
  readonly onChange: () => void;
  readonly value: string;
}) {
  return (
    <label className="availability-choice">
      <input
        checked={checked}
        disabled={disabled}
        name={name}
        onChange={onChange}
        type="radio"
        value={value}
      />
      <span>{label}</span>
    </label>
  );
}

function Checkbox({
  checked,
  disabled,
  label,
  onChange,
}: {
  readonly checked: boolean;
  readonly disabled: boolean;
  readonly label: string;
  readonly onChange: (checked: boolean) => void;
}) {
  return (
    <label className="availability-choice">
      <input
        checked={checked}
        disabled={disabled}
        onChange={(event) => {
          onChange(event.currentTarget.checked);
        }}
        type="checkbox"
      />
      <span>{label}</span>
    </label>
  );
}

function calendarDays(
  days: ReturnType<typeof availabilityWeek>,
  selected: readonly BlockSelection[],
  disabled: boolean,
  setSelected: (next: readonly BlockSelection[]) => void,
  zone: TimeZoneId,
): readonly CalendarDayProps[] {
  return days.map((day) => {
    const dayLabel = formatDate(day.dateLocal);
    return {
      dayId: day.dateLocal,
      dayLabel,
      blocks: day.blocks.map((block) => {
        const chosen = selected.some(
          (selection) =>
            selection.dateLocal === day.dateLocal && selection.blockId === block.blockId,
        );
        const bounds = `${formatTime(block.window.startsAt, zone)}–${formatTime(
          block.window.endsAt,
          zone,
        )}`;
        return {
          id: block.blockId,
          label: COPY.blockNames[block.blockId],
          accessibleName: COPY.blockAccessibleName(
            dayLabel,
            COPY.blockNames[block.blockId],
            bounds,
          ),
          selected: chosen,
          ...(disabled
            ? {}
            : {
                onToggle: () => {
                  setSelected(
                    chosen
                      ? selected.filter(
                          (selection) =>
                            selection.dateLocal !== day.dateLocal ||
                            selection.blockId !== block.blockId,
                        )
                      : [...selected, { dateLocal: day.dateLocal, blockId: block.blockId }],
                  );
                },
              }),
        };
      }),
    };
  });
}

function formatTime(at: number, zone: TimeZoneId): string {
  const wall = toWallClock(instant(at), zone);
  return `${String(wall.hour).padStart(2, "0")}:${String(wall.minute).padStart(2, "0")}`;
}

function safeWeek(
  weekStartLocal: string,
  zone: TimeZoneId,
  definitions: BlockDefinitions,
): ReturnType<typeof availabilityWeek> {
  try {
    return availabilityWeek(weekStartLocal, zone, definitions);
  } catch {
    return [];
  }
}

function safeWeekEnd(weekStartLocal: string): string | null {
  try {
    return availabilityWeekEnd(weekStartLocal);
  } catch {
    return null;
  }
}

function formatDate(dateLocal: string): string {
  try {
    const [year, month, day] = dateLocal.split("-").map(Number) as [number, number, number];
    return new Intl.DateTimeFormat("en-GB", {
      weekday: "long",
      day: "numeric",
      month: "short",
      timeZone: "UTC",
    }).format(new Date(Date.UTC(year, month - 1, day)));
  } catch {
    return "";
  }
}

function messageForIssue(issue: AvailabilityDraftIssue): string {
  return COPY.errors[issue];
}

function toggleValue<T>(values: readonly T[], value: T, checked: boolean): readonly T[] {
  return checked ? [...values, value] : values.filter((candidate) => candidate !== value);
}

function replaceWindow(
  windows: readonly {
    readonly startsAtLocal: string;
    readonly endsAtLocal: string;
    readonly helpModes: readonly AvailabilityHelpMode[];
  }[],
  index: number,
  field: "startsAtLocal" | "endsAtLocal",
  value: string,
) {
  return windows.map((window, candidateIndex) =>
    candidateIndex === index ? { ...window, [field]: value } : window,
  );
}

function asSourceChannel(value: string): AvailabilitySourceChannel {
  return value === "phone" || value === "in_person" ? value : "message";
}

const HELP_MODE_OPTIONS = [
  { value: "in_person", label: COPY.helpModes.inPerson },
  { value: "driving", label: COPY.helpModes.driving },
  { value: "phone_or_online", label: COPY.helpModes.phoneOrOnline },
] as const satisfies readonly { readonly value: AvailabilityHelpMode; readonly label: string }[];

const SOURCE_OPTIONS = [
  { value: "message", label: COPY.proxySources.message },
  { value: "phone", label: COPY.proxySources.phone },
  { value: "in_person", label: COPY.proxySources.in_person },
] as const;

export const availabilityStylesheet = String.raw`
  .availability-entry,
  .availability-overview,
  .availability-renewal {
    display: grid;
    gap: var(--herefor-space-4);
  }

  .availability-entry form,
  .availability-fieldset,
  .availability-overview__list,
  .availability-overview__windows,
  .availability-exact-times {
    display: grid;
    gap: var(--herefor-space-3);
  }

  .availability-fieldset {
    min-width: 0;
    margin: 0;
    padding: var(--herefor-space-3);
    border: 1px solid var(--herefor-color-border);
    border-radius: var(--herefor-radius-md);
  }

  .availability-choice {
    display: flex;
    gap: var(--herefor-space-2);
    align-items: center;
    min-block-size: ${targetSize.minimum};
  }

  .availability-choice input {
    inline-size: var(--herefor-space-5);
    block-size: var(--herefor-space-5);
  }

  .availability-overview__list,
  .availability-overview__windows {
    padding: 0;
    list-style: none;
  }

  .availability-overview__windows li {
    display: grid;
    gap: var(--herefor-space-1);
    padding-block: var(--herefor-space-2);
    border-block-end: 1px solid var(--herefor-color-border);
  }

  .availability-overview__batch--expired {
    padding-inline-start: var(--herefor-space-2);
    border-inline-start: 0.25rem solid var(--herefor-color-border);
  }

  .availability-overview__history {
    color: var(--herefor-color-text-muted);
  }

  @media (min-width: 48rem) {
    .availability-exact-times {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }
`;
